import React, { useEffect, useId, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import { Download, Printer, RotateCw } from "@/lib/icons";
import { Avatar } from "@/components/PhotoUpload";
import brandMark from "@/assets/pronet-logo-icon.png";

// Design canvas is authored at a fixed reference size and scaled as a whole
// via CSS transform to fit whatever width the page gives it — simpler and
// more robust for html2canvas than positioning every element by hand pixel
// coordinates over a flat background image (the previous approach).
const CARD_W = 336;
const CARD_H = 539; // matches the 54mm x 86.55mm card stock ratio
const CARD_MM_W = 54;
const CARD_MM_H = 86.55;

const FONT_STACK = "'Space Grotesk', 'Inter', system-ui, sans-serif";
const BODY_FONT_STACK = "'Inter', system-ui, sans-serif";
const INK = "#150A24";
const VIOLET_700 = "#6500CC";

// html2canvas does not reliably honor CSS text-overflow:ellipsis, so a value
// too long to fit must be truncated at the string level before render.
// Character-count heuristics guess wrong across different content and
// fonts — measuring actual glyph width via a scratch canvas is the only way
// to guarantee it never overflows, whatever the value.
let measureCtx = null;
function textWidth(text, fontPx, weight, family) {
  if (!measureCtx) measureCtx = document.createElement("canvas").getContext("2d");
  measureCtx.font = `${weight} ${fontPx}px ${family}`;
  return measureCtx.measureText(text).width;
}
function fitText(text, maxWidthPx, fontPx, weight, family = BODY_FONT_STACK) {
  const s = String(text ?? "");
  if (!s || textWidth(s, fontPx, weight, family) <= maxWidthPx) return s;
  let lo = 0, hi = s.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    const candidate = `${s.slice(0, mid).trimEnd()}…`;
    if (textWidth(candidate, fontPx, weight, family) <= maxWidthPx) lo = mid; else hi = mid - 1;
  }
  return lo <= 0 ? "…" : `${s.slice(0, lo).trimEnd()}…`;
}
// Shrinks a headline down to a floor before falling back to truncation, so a
// long studio name loses size gracefully instead of getting chopped early.
function fitHeadingFontSize(text, maxWidthPx, basePx, minPx, weight, family = FONT_STACK) {
  const full = textWidth(text, basePx, weight, family);
  if (!text || full <= maxWidthPx) return basePx;
  return Math.max(minPx, basePx * (maxWidthPx / full));
}

const CURVE_TOP = "M0,0 H336 V78 C 258,110 214,58 148,82 C 84,105 42,70 0,90 Z";
const CURVE_TOP_SOFT = "M0,0 H336 V48 C 264,84 208,44 140,66 C 76,88 36,52 0,72 Z";
const CURVE_BOTTOM = "M0,96 H336 V34 C 258,14 214,44 148,26 C 84,9 42,32 0,18 Z";
const CURVE_BOTTOM_SOFT = "M0,96 H336 V50 C 264,32 208,58 140,40 C 76,24 36,46 0,32 Z";

function CardCurves({ gradId, softId }) {
  return (
    <>
      <svg className="idc-curve idc-curve-top" viewBox="0 0 336 132" preserveAspectRatio="none">
        <path d={CURVE_TOP} fill={`url(#${softId})`} opacity="0.55" />
        <path d={CURVE_TOP_SOFT} fill={`url(#${gradId})`} />
      </svg>
      <svg className="idc-curve idc-curve-bottom" viewBox="0 0 336 96" preserveAspectRatio="none">
        <path d={CURVE_BOTTOM} fill={`url(#${softId})`} opacity="0.55" />
        <path d={CURVE_BOTTOM_SOFT} fill={`url(#${gradId})`} />
      </svg>
    </>
  );
}

const RowIcon = {
  member: (p) => <svg {...p} viewBox="0 0 24 24" fill="none"><rect x="3.5" y="4.5" width="17" height="15" rx="2.5" stroke="currentColor" strokeWidth="1.6"/><path d="M8 9h8M8 13h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>,
  category: (p) => <svg {...p} viewBox="0 0 24 24" fill="none"><rect x="3.5" y="7.5" width="17" height="11.5" rx="2" stroke="currentColor" strokeWidth="1.6"/><path d="M8.5 7.5V6a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v1.5" stroke="currentColor" strokeWidth="1.6"/></svg>,
  city: (p) => <svg {...p} viewBox="0 0 24 24" fill="none"><path d="M12 21s7-6.1 7-11.5A7 7 0 0 0 5 9.5C5 14.9 12 21 12 21z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/><circle cx="12" cy="9.5" r="2.3" stroke="currentColor" strokeWidth="1.6"/></svg>,
  pincode: (p) => <svg {...p} viewBox="0 0 24 24" fill="none"><path d="M9.5 3.5 7.5 20.5M16.5 3.5l-2 17M4 9h17M3 15h17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>,
  mobile: (p) => <svg {...p} viewBox="0 0 24 24" fill="none"><rect x="6.5" y="2.5" width="11" height="19" rx="2.5" stroke="currentColor" strokeWidth="1.6"/><path d="M10.5 18.3h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>,
  email: (p) => <svg {...p} viewBox="0 0 24 24" fill="none"><rect x="3.5" y="5.5" width="17" height="13" rx="2.2" stroke="currentColor" strokeWidth="1.6"/><path d="M4.5 7 12 12.5 19.5 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  blood: (p) => <svg {...p} viewBox="0 0 24 24" fill="none"><path d="M12 3.5s6 6.4 6 10.6a6 6 0 0 1-12 0c0-4.2 6-10.6 6-10.6z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/></svg>,
};

function Row({ icon, label, value, fontPx }) {
  const Icon = RowIcon[icon];
  return (
    <div className="idc-row">
      <Icon className="idc-row-icon" width="15" height="15" />
      <div className="idc-row-label">{label}</div>
      <div className="idc-row-value" style={fontPx ? { fontSize: fontPx } : undefined}>{value}</div>
    </div>
  );
}

export default function IdCard({ member, showActions = true, initialSide = "front" }) {
  const wrapRef = useRef(null);
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [scale, setScale] = useState(0);
  const [side, setSide] = useState(initialSide);
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const gradId = `idcGrad${uid}`;
  const softId = `idcSoft${uid}`;
  const profileUrl = `${window.location.origin}/member/${encodeURIComponent(member.member_id)}`;

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setScale(el.offsetWidth / CARD_W);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const rawHeading = (member.studio_name || "").trim() || member.category || "";
  const values = {
    memberId: member.member_id || "-",
    category: member.category || "Not added",
    city: member.city || "Not added",
    pincode: member.pincode || "-",
    mobile: member.mobile || "-",
    email: member.email || "Not added",
    blood: member.blood_group || "-",
  };

  // A plain timeout, not double-rAF: requestAnimationFrame never fires for a
  // backgrounded/non-visible tab, which would hang this indefinitely — a
  // timeout settles the same React commit regardless of tab visibility.
  const waitPaint = () => new Promise((r) => setTimeout(r, 50));

  const waitFontsReady = () => (document.fonts?.ready ? document.fonts.ready.catch(() => {}) : Promise.resolve());

  // html2canvas does not reliably honor a CSS `transform: scale()` on the
  // element it's asked to capture — it was silently clipping the right edge
  // and dropping the SVG curve header/footer entirely (both are sized off
  // percentages of the parent, which the transform confuses it about).
  // The transform only exists so the card fits whatever width the page
  // gives it on screen; for capture, zero it out so html2canvas always sees
  // the card at its true native 336x539 box, then restore it afterwards.
  const capture = async () => {
    await waitFontsReady();
    const el = cardRef.current;
    const prevTransform = el.style.transform;
    el.style.transform = "none";
    try {
      await waitPaint();
      return await html2canvas(el, { scale: 3, backgroundColor: "#ffffff", useCORS: true, allowTaint: true });
    } finally {
      el.style.transform = prevTransform;
    }
  };

  const downloadPdf = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    const startSide = side;
    try {
      setSide("front");
      await waitPaint();
      const frontCanvas = await capture();
      setSide("back");
      await waitPaint();
      const backCanvas = await capture();

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: [CARD_MM_W, CARD_MM_H] });
      pdf.addImage(frontCanvas.toDataURL("image/png"), "PNG", 0, 0, CARD_MM_W, CARD_MM_H, undefined, "FAST");
      pdf.addPage([CARD_MM_W, CARD_MM_H], "portrait");
      pdf.addImage(backCanvas.toDataURL("image/png"), "PNG", 0, 0, CARD_MM_W, CARD_MM_H, undefined, "FAST");
      pdf.save(`PRONET_${member.member_id}.pdf`);
    } catch (e) {
      console.error("ID card PDF export failed", e);
      toast.error("Couldn't prepare the PDF. Please try again.");
    } finally {
      setSide(startSide);
      setBusy(false);
    }
  };

  const printCard = () => {
    if (!cardRef.current) return;
    document.body.classList.add("printing-id-card");
    window.print();
    setTimeout(() => document.body.classList.remove("printing-id-card"), 800);
  };

  // Row values share one font size; only the available width (from each
  // row's label edge to the card's right margin) differs meaningfully, so a
  // single measured size for the tightest case is used everywhere.
  const rowFontPx = 12.5;
  const rowMaxW = CARD_W - 22 - 22 - 64 - 10; // card padding + label column + icon gap
  const fitRow = (key) => fitText(values[key], rowMaxW, rowFontPx, 600);

  const nameFontPx = 19;
  const nameMaxW = CARD_W * 0.86;
  const nameText = fitText(member.full_name, nameMaxW, nameFontPx, 700, FONT_STACK);

  const headingMaxW = CARD_W * 0.78;
  const headingBasePx = 11;
  const headingFontPx = fitHeadingFontSize(rawHeading, headingMaxW, headingBasePx, headingBasePx * 0.7, 600);
  const headingText = fitText(rawHeading, headingMaxW, headingFontPx, 600);

  return (
    <div className="space-y-4">
      <div
        ref={wrapRef}
        data-testid="id-card"
        className="id-card relative"
        style={{ width: "100%", maxWidth: `${CARD_MM_W * 8}px`, aspectRatio: `${CARD_W} / ${CARD_H}` }}
      >
        {scale > 0 && (
          <div
            ref={cardRef}
            className="idc-card"
            style={{
              width: CARD_W, height: CARD_H,
              transform: `scale(${scale})`, transformOrigin: "top left",
            }}
          >
            <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
              <defs>
                <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#7F00FF" />
                  <stop offset="100%" stopColor="#B54AFF" />
                </linearGradient>
                <linearGradient id={softId} x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#4B0099" />
                  <stop offset="100%" stopColor="#7F00FF" />
                </linearGradient>
              </defs>
            </svg>

            <CardCurves gradId={gradId} softId={softId} />

            <div className="idc-body">
              <div className="idc-brand-row">
                <img className="idc-brand-mark" src={brandMark} alt="PRONET" />
                <div className="idc-brand-name">PRONET</div>
              </div>

              {side === "front" ? (
                <>
                  <div className="idc-photo-wrap">
                    <div className="idc-photo-glow" />
                    <div className="idc-photo-ring">
                      <div className="idc-photo-circle">
                        <Avatar src={member.photo_url} name={member.full_name} avatarKey={member.avatar_key} size={96} bordered={false} crossOrigin="anonymous" />
                      </div>
                    </div>
                    <div className="idc-verified-badge">
                      <svg viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="white" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                  </div>

                  <div data-testid="id-card-name" title={member.full_name} className="idc-id-name" style={{ fontSize: nameFontPx }}>
                    {nameText}
                  </div>
                  <div data-testid="id-card-heading" className="idc-id-role" style={{ fontSize: headingFontPx }}>
                    {headingText}
                  </div>

                  <div className="idc-rows">
                    <div data-testid="id-card-memberId"><Row icon="member" label="Member ID" value={fitRow("memberId")} /></div>
                    <div data-testid="id-card-profession"><Row icon="category" label="Category" value={fitRow("category")} /></div>
                    <div data-testid="id-card-cityDist"><Row icon="city" label="City" value={fitRow("city")} /></div>
                    <div data-testid="id-card-pincode"><Row icon="pincode" label="Pincode" value={fitRow("pincode")} /></div>
                  </div>
                </>
              ) : (
                <>
                  <div className="idc-qr-panel">
                    <QRCodeCanvas value={profileUrl} size={100} level="M" includeMargin={false} bgColor="#F2ECFA" fgColor={INK} />
                  </div>
                  <div data-testid="id-card-qr" className="idc-qr-caption">Scan to view public profile</div>

                  <div className="idc-rows" style={{ marginTop: 18 }}>
                    <div data-testid="id-card-mobile"><Row icon="mobile" label="Mobile" value={fitRow("mobile")} /></div>
                    <div data-testid="id-card-email"><Row icon="email" label="Email" value={fitRow("email")} /></div>
                    <div data-testid="id-card-bloodGroup"><Row icon="blood" label="Blood Grp" value={fitRow("blood")} /></div>
                  </div>

                  <div className="idc-verify-line">
                    <svg viewBox="0 0 24 24" fill="none"><path d="M12 3l7 3v5.5c0 4.7-3 8.2-7 9.5-4-1.3-7-4.8-7-9.5V6l7-3z" stroke={VIOLET_700} strokeWidth="1.6" strokeLinejoin="round"/><path d="M9 12l2 2 4-4.2" stroke={VIOLET_700} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    <span>PRONET Pro ID Member</span>
                  </div>

                  <div className="idc-notice">
                    Property of Photographers Network. Valid for PRONET members only.<br />
                    If found, please contact photographersnetwork.in
                  </div>
                </>
              )}

              <div className="idc-footer"><span>photographersnetwork.in</span></div>
            </div>
          </div>
        )}
      </div>

      {showActions && (
        <div className="flex flex-wrap gap-2 no-print">
          <button
            data-testid="id-card-flip"
            onClick={() => setSide((s) => (s === "front" ? "back" : "front"))}
            className="btn-ghost inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white"
          >
            <RotateCw size={14} /> View {side === "front" ? "back" : "front"}
          </button>
          <button
            data-testid="id-card-download"
            onClick={downloadPdf}
            disabled={busy}
            className="btn-glow inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
          >
            <Download size={14} /> {busy ? "Preparing…" : "Download PDF"}
          </button>
          <button
            data-testid="id-card-print"
            onClick={printCard}
            className="btn-ghost inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white"
          >
            <Printer size={14} /> Print {side === "front" ? "front" : "back"}
          </button>
        </div>
      )}
    </div>
  );
}
